// Zod schemas for form validation and API responses
// These schemas align with the PRD data model and Firestore user collection structure

import { z } from 'zod';

// Display name source enum schema
export const displayNameSourceSchema = z.enum(['user', 'email', 'auto']);

// Allowed photo URL domains for security
const ALLOWED_PHOTO_DOMAINS = [
  'firebasestorage.googleapis.com',
  'storage.googleapis.com',
  's3.amazonaws.com',
  'cloudinary.com',
  'imgur.com',
  'gravatar.com',
];

// Enhanced photo URL validation with domain restrictions
const photoUrlValidator = z
  .string()
  .url('Must be a valid URL')
  .refine(url => {
    try {
      const urlObj = new URL(url);
      return ALLOWED_PHOTO_DOMAINS.some(
        domain =>
          urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
      );
    } catch {
      return false;
    }
  }, 'Photo URL must be from an allowed domain (Firebase Storage, Google, Facebook, AWS S3, Cloudinary, Imgur, or Gravatar)')
  .refine(url => {
    // Additional security: check file extension
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname.toLowerCase();
      return (
        pathname.includes('.jpg') ||
        pathname.includes('.jpeg') ||
        pathname.includes('.png') ||
        pathname.includes('.webp') ||
        pathname === '' || // Some profile URLs may not have extensions
        !pathname.includes('.')
      ); // URLs without extensions
    } catch {
      return false;
    }
  }, 'Photo URL must point to a valid image file (jpg, jpeg, png, webp) or valid profile endpoint');

// Photo URLs schema with multiple sizes and enhanced validation
export const photoUrlsSchema = z
  .object({
    128: photoUrlValidator.optional(), // 128px JPEG version
    512: photoUrlValidator.optional(), // 512px JPEG version
  })
  .optional()
  .nullable();

// Display name validation (1-30 characters, new Unicode rules as per issue #388)
export const displayNameSchema = z
  .string()
  .min(1, 'Display name is required')
  .regex(
    /^[\p{L}\p{Mn}\p{Mc}\p{Nd}\p{Pd}\s'_.]+$/u,
    "Display name can only contain letters, numbers, spaces, hyphens (-), apostrophes ('), underscores (_), and periods (.)"
  )
  .refine(
    val => Array.from(val).length <= 30,
    'Display name cannot exceed 30 characters'
  )
  .refine(
    val => val.trim().length >= 1,
    'Display name cannot be empty or contain only spaces'
  )
  .refine(
    val => val === val.trim(),
    'Display name cannot have leading or trailing spaces'
  )
  .refine(
    val => !val.trim().startsWith('-') && !val.trim().endsWith('-'),
    'Display name cannot start or end with hyphens'
  );

// Core user profile schema based on Firestore document structure
// Stored data is parsed permissively; write rules belong to input schemas (#2325).
export const userProfileSchema = z.object({
  uid: z.string().min(1, 'User ID is required'),
  display_name: z.string(),
  display_name_source: displayNameSourceSchema,
  email_verified: z.boolean(),
  photo_url: z
    .object({
      128: z.string().optional(),
      512: z.string().optional(),
    })
    .nullable()
    .optional(),
  created_at: z.date(),
  updated_at: z.date().optional(),
});

// User update input schema
export const updateUserInputSchema = z.object({
  display_name: displayNameSchema.optional(),
  display_name_source: displayNameSourceSchema.optional(),
  photo_url: photoUrlsSchema,
});

// Admin user update schema
export const adminUpdateUserInputSchema = updateUserInputSchema.extend({});

// Subscription status enum schema
export const subscriptionStatusSchema = z.enum([
  'ACTIVE',
  'TRIALING',
  'GRACE_PERIOD',
  'PAST_DUE',
  'CANCELED',
  'PAUSED',
]);

// Subscription schema
export const subscriptionSchema = z.object({
  uid: z.string().min(1, 'User ID is required'),
  status: subscriptionStatusSchema,
  tier: z.string().min(1, 'Subscription tier is required'),
  current_period_end: z.date(),
  created_at: z.date(),
  updated_at: z.date(),
});

// Interaction type enum schema
export const interactionTypeSchema = z.enum([
  'VIDEO_CALL',
  'LIVE_CHAT',
  'MEETUP',
  'WORKSHOP',
  'CONFERENCE',
  'PRIVATE_SESSION',
]);

// Badge schema
export const badgeSchema = z.object({
  badgeId: z.string().min(1, 'Badge ID is required'),
  name: z.string().min(1, 'Badge name is required'),
  icon_url: z.string().url('Badge icon must be a valid URL'),
  description: z.string().min(1, 'Badge description is required'),
  created_at: z.date(),
});

// User badge schema (for user's earned badges)
export const userBadgeSchema = z.object({
  badgeId: z.string().min(1, 'Badge ID is required'),
  earned_at: z.date(),
});

// Interaction schema
export const interactionSchema = z.object({
  interactionId: z.string().min(1, 'Interaction ID is required'),
  type: interactionTypeSchema,
  title: z.string().min(1, 'Interaction title is required'),
  description: z.string().min(1, 'Interaction description is required'),
  occurred_at: z.date(),
  created_at: z.date(),
});

// User interaction schema (for user's recorded interactions)
export const userInteractionSchema = z.object({
  interactionId: z.string().min(1, 'Interaction ID is required'),
  recorded_at: z.date(),
});

// Form schemas for authentication
export const signUpSchema = z.object({
  email: z
    .string()
    .email('Please enter a valid email address (e.g., user@example.com)'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters long')
    // The API's ceiling (openapi.yaml `maxLength`), which is also the
    // Identity Platform password policy's (#2084).
    .max(4096, 'Password must be at most 4096 characters long')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must include at least one lowercase letter, one uppercase letter, and one number'
    ),
  display_name: displayNameSchema,
  access_code: z.string().optional(),
});

export const signInSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

// Profile completion schema for onboarding flow
export const profileCompletionSchema = z.object({
  display_name: displayNameSchema,
});

// Profile page form: the display name only; the avatar has its own path (#2332)
export const profileFormSchema = z.object({
  display_name: displayNameSchema,
});

// User query parameters schema
export const userQueryParamsSchema = z.object({
  query: z.string().optional(), // Search query
  limit: z.number().min(1).max(100).default(20), // Results limit
  offset: z.string().optional(), // Pagination offset
});

// User search result schema
export const userSearchResultSchema = z.object({
  users: z.array(userProfileSchema),
  total: z.number().min(0),
  hasMore: z.boolean(),
  nextOffset: z.string().optional(),
});

// API response schemas
export const apiErrorSchema = z.object({
  error: z.string(),
  message: z.string(),
  code: z.string().optional(),
});

export const apiSuccessSchema = z.object({
  success: z.boolean().default(true),
  message: z.string().optional(),
});

// Export TypeScript types from schemas
// Note: PhotoUrls and UserProfile are imported from '../lib/types/user.ts' to avoid conflicts
export type UpdateUserInput = z.infer<typeof updateUserInputSchema>;
export type AdminUpdateUserInput = z.infer<typeof adminUpdateUserInputSchema>;
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;
export type UserSubscription = z.infer<typeof subscriptionSchema>;
export type InteractionType = z.infer<typeof interactionTypeSchema>;
export type BadgeDefinition = z.infer<typeof badgeSchema>;
export type UserBadge = z.infer<typeof userBadgeSchema>;
export type InteractionDefinition = z.infer<typeof interactionSchema>;
export type UserInteraction = z.infer<typeof userInteractionSchema>;
export type SignUpData = z.infer<typeof signUpSchema>;
export type SignInData = z.infer<typeof signInSchema>;
export type ProfileCompletionData = z.infer<typeof profileCompletionSchema>;
export type ProfileFormData = z.infer<typeof profileFormSchema>;
export type UserQueryParams = z.infer<typeof userQueryParamsSchema>;
export type UserSearchResult = z.infer<typeof userSearchResultSchema>;
export type ApiError = z.infer<typeof apiErrorSchema>;
export type ApiSuccess = z.infer<typeof apiSuccessSchema>;
